import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getPortalProfile, updatePortalProfile, uploadFile, getFiles, deleteFile } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { User, FileText, Upload, Trash, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PortalProfile = () => {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    national_id: '',
    passport_number: '',
    date_of_birth: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      const [profileRes, filesRes] = await Promise.all([
        getPortalProfile(),
        getFiles({ client_id: user?.id })
      ]);
      const profileData = profileRes.data;
      setProfile(profileData);
      setFormData({
        name: profileData?.name || user?.name || '',
        phone: profileData?.phone || '',
        address: profileData?.address || '',
        national_id: profileData?.national_id || '',
        passport_number: profileData?.passport_number || '',
        date_of_birth: profileData?.date_of_birth || '',
        notes: profileData?.notes || ''
      });
      setDocuments(filesRes.data || []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updatePortalProfile(formData);
      toast.success('Profile updated');
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('client_id', user?.id);
      formDataUpload.append('category', 'client_document');
      await uploadFile(formDataUpload);
      toast.success('Document uploaded');
      loadData();
    } catch (err) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deleteFile(fileId);
      toast.success('Document deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const profileComplete = formData.name && formData.phone && (formData.national_id || formData.passport_number);

  return (
    <div data-testid="portal-profile">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight">{t('nav.profile')}</h1>
        <p className="text-muted-foreground mt-1">Manage your personal information and documents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Form */}
        <div className="lg:col-span-2">
          <div className="card-brutalist">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                {formData.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div>
                <h2 className="font-bold">{formData.name || 'Client'}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
              {profileComplete && (
                <div className="ml-auto flex items-center gap-1 text-green-600 text-sm">
                  <CheckCircle weight="fill" className="w-4 h-4" />
                  Profile Complete
                </div>
              )}
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('clients.name')} *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="input-brutalist"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('clients.phone')} *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="input-brutalist"
                    data-testid="profile-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('clients.address')}</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-brutalist"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('clients.nationalId')}</Label>
                  <Input
                    value={formData.national_id}
                    onChange={(e) => setFormData({ ...formData, national_id: e.target.value })}
                    placeholder="Required for customs"
                    className="input-brutalist"
                    data-testid="profile-national-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('clients.passport')}</Label>
                  <Input
                    value={formData.passport_number}
                    onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                    className="input-brutalist"
                    data-testid="profile-passport-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('clients.dob')}</Label>
                <Input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="input-brutalist"
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button type="submit" disabled={saving} className="btn-primary" data-testid="profile-save-btn">
                  {saving ? 'Saving...' : t('common.save')}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Documents */}
        <div>
          <div className="card-brutalist">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold">{t('clients.documents')}</h3>
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  data-testid="document-upload-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  size="sm"
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground mb-4">
              Upload your passport, ID card, or other required documents (PDF, JPG, PNG)
            </p>

            {documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border border-dashed border-border">
                <FileText weight="duotone" className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                <p className="text-sm">No documents uploaded</p>
              </div>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 border border-border">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText weight="duotone" className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                        <p className="text-xs text-muted-foreground">{(doc.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteFile(doc.id)}
                      className="text-destructive flex-shrink-0"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalProfile;
